import { WATTS_IN_KW, WATTS_IN_MW } from '@/constants/units'

export type PowerUnit = 'watts' | 'kilowatts' | 'megawatts' | 'gigawatts'
export type EnergyUnit = 'watt_hours' | 'kilowatt_hours' | 'megawatts_hours'

// kW and MW come from @/constants/units; GW has no constant there and nothing else needs one.
const WATTS_TO_GW = 1_000_000_000

export const UNIT_SYMBOLS: Record<PowerUnit | EnergyUnit, string> = {
  watts: 'W',
  watt_hours: 'Wh',
  kilowatts: 'kW',
  kilowatt_hours: 'kWh',
  megawatts: 'MW',
  megawatts_hours: 'MWh',
  gigawatts: 'GW',
}

/**
 * Converts and formats power values in watts to the appropriate units.
 */
export function convertEnergyPower(power: number, unit: PowerUnit | EnergyUnit) {
  switch (unit) {
    case 'gigawatts':
      return `${power / WATTS_TO_GW} ${UNIT_SYMBOLS.gigawatts}`
    case 'megawatts':
      return `${power / WATTS_IN_MW} ${UNIT_SYMBOLS.megawatts}`
    case 'kilowatts':
      return `${power / WATTS_IN_KW} ${UNIT_SYMBOLS.kilowatts}`
    case 'kilowatt_hours':
      return `${power / WATTS_IN_KW} ${UNIT_SYMBOLS.kilowatt_hours}`
    // TRAP: `megawatts_hours` and `watt_hours` have no case, so both fall through here and return an
    // UNSCALED value carrying a `W` symbol — an energy quantity labelled as power. No CURRENT caller
    // reaches it: the four non-literal call sites (`ActivationGroupsDataGrid.tsx:103,114` and
    // `ActivationGroupCard.tsx:58,66`, via the `unit` const on :30) are all ternaries over
    // `'kilowatts' | 'kilowatt_hours'`. A new caller invalidates that silently, so do not rely on it.
    // Adding the missing cases would be safe. Prefer `formatEnergyWh` below for new energy formatting.
    default:
      return `${power} ${UNIT_SYMBOLS.watts}`
  }
}

export function formatEnergyPower(power: number, unit: PowerUnit | EnergyUnit): string {
  return `${power.toFixed(2)} ${UNIT_SYMBOLS[unit]}`
}

/**
 * Whether a battery has actually reported a usable RATED ENERGY, specifically. Zero counts as "no
 * reading" rather than "a battery of size zero", because no real battery has one and displaying
 * "0.00 MWh" on an axis reads as a measurement. Which of 0 or null actually arrives for an
 * unreported field is not settled — `BatteryInfoResponse` documents the BFF field as nullable, and
 * nothing on this side can verify what the controller sends — so both are rejected here.
 *
 * Do NOT reuse this on fields where zero is a legitimate reading (`energyToFullCharge` is 0 on a full
 * battery); the name is about rated energy and so is the zero rule.
 *
 * Anything non-finite is a malformed payload — JSON cannot carry NaN or Infinity, so their presence
 * means something upstream computed them.
 *
 * Call this rather than re-spelling the condition, so every surface displaying rated energy agrees on
 * what counts as unknown. Two spellings that look equivalent are not: `typeof x === 'number' && x > 0`
 * admits Infinity, and `typeof x !== 'number' || x <= 0` is not the negation, because NaN is
 * typeof 'number' while `NaN <= 0` is false. Both would render their value onto an axis label.
 *
 * The `typeof` conjunct is required first: `Number.isFinite` is typed `(n: unknown) => boolean` and
 * does not narrow, so `value > 0` would not compile without it.
 */
export function isReportedEnergyWh(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

/**
 * Picks the display unit for an energy axis from its magnitude — kWh below 1 MWh, MWh at or above.
 *
 * `domainWh` is the LARGEST value on the axis, not the value being formatted: every tick on one axis
 * must resolve to the same unit or the axis is unreadable.
 *
 * `WATTS_IN_KW` / `WATTS_IN_MW` are named for power but are reused here as Wh divisors — the
 * magnitude step Wh → kWh → MWh is the same 1000× step as W → kW → MW.
 */
function pickEnergyUnit(domainWh: number): { symbol: string; divisor: number } {
  return Math.abs(domainWh) >= WATTS_IN_MW
    ? { symbol: UNIT_SYMBOLS.megawatts_hours, divisor: WATTS_IN_MW }
    : { symbol: UNIT_SYMBOLS.kilowatt_hours, divisor: WATTS_IN_KW }
}

/**
 * How many decimals an energy axis shows, given its domain already scaled into the display unit.
 * Three tiers so a mid-scale axis keeps one significant digit: at or above 100 the integer part
 * already carries three digits, between 10 and 100 one decimal is what keeps quarter ticks distinct,
 * and below 10 two decimals are needed for the same reason.
 */
function decimalsForScaledDomain(scaledDomain: number): number {
  if (scaledDomain >= 100) return 0
  if (scaledDomain >= 10) return 1
  return 2
}

/**
 * Formats a Wh value in the unit chosen for `domainWh`. Decimal count is decided from the DOMAIN
 * rather than the individual value, so ticks on one axis line up ("0.00 / 0.63 / 1.25 / 2.50 MWh")
 * instead of mixing widths ("0.00 / 0.63 / 1 / 3").
 *
 * `domainWh` defaults to `wh`, for the single-value case where the value IS the whole domain and so
 * picks its own natural unit. Callers formatting a set of ticks must pass the domain explicitly.
 *
 * `domainWh` must be a reported energy — see `isReportedEnergyWh`. Nothing here rejects a bad one, and
 * the result is not uniform: a zero or NaN domain falls to kWh with 2 decimals, while Infinity picks
 * MWh with none. Guard at the call site rather than relying on any of that.
 *
 * Because the unit and the decimals both come from the same domain, a domain just under the MWh
 * threshold renders 0-decimal kWh: 999_999 Wh reads "1000 kWh", not "1.00 MWh". Intended — one axis,
 * one unit. Promoting it to MWh at display precision was rejected: it would put the effective
 * threshold at 999_500 while this code states `>= WATTS_IN_MW`, and would label a sub-MWh battery
 * MWh. Only domains within 500 Wh of 1 MWh are affected.
 */
export function formatEnergyWh(wh: number, domainWh: number = wh): string {
  const { symbol, divisor } = pickEnergyUnit(domainWh)
  const decimals = decimalsForScaledDomain(Math.abs(domainWh / divisor))
  // Format, parse, format: a small negative's first toFixed keeps the sign at whatever the tier's
  // width is — (-0.001).toFixed(2) is "-0.00", .toFixed(1) is "-0.0", .toFixed(0) is "-0". Each
  // parses to the number -0, and `(-0).toFixed(n)` is unsigned. A single toFixed would keep the sign.
  const scaled = Number((wh / divisor).toFixed(decimals))
  return `${scaled.toFixed(decimals)} ${symbol}`
}

export function wattHoursToMegawattHours(wattHours: number): number {
  return wattHours / WATTS_IN_MW
}

export function megawattHoursToWattHours(megawattHours: number): number {
  return megawattHours * WATTS_IN_MW
}
